import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import TimerIcon from '@mui/icons-material/Timer';
import LockIcon from '@mui/icons-material/LockOutlined';
import { useLocation } from 'react-router-dom';

const WARNING_COUNTDOWN_SECONDS = 30;

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: 16,
    boxShadow: '0 8px 32px rgba(0, 82, 204, 0.12)',
    maxWidth: 440,
    padding: theme.spacing(1),
    backdropFilter: 'blur(10px)',
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  padding: theme.spacing(3, 3, 2),
  textAlign: 'center',
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(2, 3, 3),
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(2, 3, 3),
  justifyContent: 'center',
}));

const ProgressBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginTop: theme.spacing(3),
  marginBottom: theme.spacing(2),
  position: 'relative',
}));

const TimerText = styled(Typography)(({ theme }) => ({
  position: 'absolute',
  fontWeight: 700,
  fontSize: '1.5rem',
  color: theme.palette.primary.main,
}));

const StyledButton = styled(Button)(({ theme }) => ({
  padding: theme.spacing(1, 4),
  fontSize: '1rem',
  fontWeight: 600,
  borderRadius: 10,
  textTransform: 'none',
  boxShadow: '0 4px 12px rgba(0, 82, 204, 0.15)',
  '&:hover': {
    boxShadow: '0 6px 16px rgba(0, 82, 204, 0.25)',
    transform: 'translateY(-2px)',
    transition: 'all 0.2s ease-in-out',
  },
}));

const WarningIcon = styled(Box)(({ theme }) => ({
  width: 72,
  height: 72,
  borderRadius: '50%',
  background: `linear-gradient(135deg, ${theme.palette.primary.light}20, ${theme.palette.primary.main}15)`,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  margin: '0 auto',
  marginBottom: theme.spacing(2),
  boxShadow: '0 6px 16px rgba(0, 82, 204, 0.08)',
  border: `1px solid ${theme.palette.primary.main}20`,
  '& svg': {
    fontSize: 36,
    color: theme.palette.primary.main,
  },
}));

const IdleWarningDialog = ({ open, onContinue }) => {
  const [countdown, setCountdown] = useState(WARNING_COUNTDOWN_SECONDS);
  const [autoSaveStatus, setAutoSaveStatus] = useState('');
  const location = useLocation();
  const isApplyLoanPage = location.pathname === '/apply-loan';
  const autoSaveTriggeredRef = useRef(false);
  const timeoutTriggeredRef = useRef(false);

  useEffect(() => {
    let timer;
    if (open) {
      setCountdown(WARNING_COUNTDOWN_SECONDS);
      autoSaveTriggeredRef.current = false;
      timeoutTriggeredRef.current = false;

      timer = setInterval(() => {
        setCountdown((prevCount) => {
          if (prevCount <= 1 && !timeoutTriggeredRef.current) {
            clearInterval(timer);
            timeoutTriggeredRef.current = true;

            if (isApplyLoanPage && !autoSaveTriggeredRef.current) {
              autoSaveTriggeredRef.current = true;

              localStorage.removeItem('temp_logout_token');
              localStorage.removeItem('sessionTimeoutInProgress');

              const timestamp = Date.now();
              window.dispatchEvent(
                new CustomEvent('sessionTimeout', {
                  detail: {
                    autoSave: true,
                    timestamp: timestamp,
                  },
                })
              );
              setAutoSaveStatus('Saving application draft...');

              setTimeout(() => {
                if (!timeoutTriggeredRef.current) return;
                window.dispatchEvent(new CustomEvent('immediateTimeout'));
              }, 2000);
            } else {
              window.dispatchEvent(new CustomEvent('immediateTimeout'));
            }
            return 0;
          }
          return prevCount - 1;
        });
      }, 1000);
    } else {
      clearInterval(timer);
      setAutoSaveStatus('');
      autoSaveTriggeredRef.current = false;
      timeoutTriggeredRef.current = false;
    }

    return () => {
      clearInterval(timer);
    };
  }, [open, isApplyLoanPage]);

  const progress = (countdown / WARNING_COUNTDOWN_SECONDS) * 100;

  return (
    <StyledDialog
      open={open}
      aria-labelledby="idle-warning-dialog-title"
      aria-describedby="idle-warning-dialog-description"
    >
      <StyledDialogTitle id="idle-warning-dialog-title">
        <WarningIcon>
          <LockIcon />
        </WarningIcon>
        <Typography variant="h5" component="div" fontWeight={700}>
          Session Timeout Warning
        </Typography>
      </StyledDialogTitle>

      <Divider variant="middle" />

      <StyledDialogContent>
        <DialogContentText
          id="idle-warning-dialog-description"
          sx={{ textAlign: 'center', color: 'text.primary', mb: 1 }}
        >
          Your session is about to expire due to inactivity. For security reasons, you will be automatically
          logged out shortly.
          {isApplyLoanPage && (
            <Typography variant="body2" color="primary" sx={{ mt: 1 }}>
              Don't worry - your loan application will be automatically saved as a draft.
            </Typography>
          )}
        </DialogContentText>

        <ProgressBox>
          <CircularProgress variant="determinate" value={progress} size={100} thickness={5} color="primary" />
          <TimerText>{countdown}</TimerText>
        </ProgressBox>

        <Typography variant="subtitle2" align="center" color="text.secondary">
          seconds remaining
        </Typography>

        {autoSaveStatus && (
          <Typography variant="body2" align="center" color="primary" sx={{ mt: 2 }}>
            {autoSaveStatus}
          </Typography>
        )}
      </StyledDialogContent>

      <StyledDialogActions>
        <StyledButton onClick={onContinue} color="primary" variant="contained" size="large" startIcon={<TimerIcon />}>
          Continue Session
        </StyledButton>
      </StyledDialogActions>
    </StyledDialog>
  );
};

export default IdleWarningDialog;


